import { imagePositionStyle, responsiveImageProps } from "../../lib/image-position";
import { ChevronsLeftRight } from "lucide-react";
import React from "react";
import { cn } from "../../lib/utils";

type ComparisonAspectRatio = "4:3" | "16:9" | "1:1" | "3:4";

/** Seam position = percent of the BEFORE photo visible from the left. */
type ComparisonStartPosition = "half" | "mostly-after" | "mostly-before";

const ASPECT_CLASS: Record<ComparisonAspectRatio, string> = {
    "16:9": "jobsite:aspect-[16/9]",
    "1:1": "jobsite:aspect-square",
    "3:4": "jobsite:aspect-[3/4]",
    "4:3": "jobsite:aspect-[4/3]",
  },
  /** Hide a corner label once the seam gets close enough to sit under it. */
  LABEL_FADE_MARGIN = 18,

  START_POSITION_PERCENT: Record<ComparisonStartPosition, number> = {
    half: 50,
    "mostly-after": 30,
    "mostly-before": 70,
  },
  clampPercent = (value: number): number => Math.min(100, Math.max(0, value));

interface ComparisonFrameProps {
  beforeSrc: string;
  afterSrc: string;
  beforeAlt: string;
  afterAlt: string;
  /** Empty string means no chip; callers supply defaults via defaultProps. */
  beforeLabel?: string;
  afterLabel?: string;
  beforeFocus?: string;
  afterFocus?: string;
  aspectRatio?: ComparisonAspectRatio;
  startPosition?: ComparisonStartPosition;
  /** First comparison on a page loads eagerly; the rest lazily. */
  eager?: boolean;
  /** Aria-label for the slider handle, e.g. "Compare before and after: Full roof replacement". */
  groupLabel: string;
  /** One small seam wiggle when the frame first scrolls into view, so visitors find the drag. */
  wiggle?: boolean;
  className?: string;
}

/**
 * The on-image comparison: before on the left of the seam, after on the right,
 * always (reading order is time order). Dragging the handle or tapping the
 * photo moves the seam; the handle is the only element that captures pointers,
 * so vertical swipes over the photo still scroll the page.
 */
const ComparisonFrame = ({
  beforeSrc,
  afterSrc,
  beforeAlt,
  afterAlt,
  beforeLabel,
  afterLabel,
  beforeFocus,
  afterFocus,
  aspectRatio = "4:3",
  startPosition = "half",
  eager = false,
  groupLabel,
  wiggle = true,
  className,
}: ComparisonFrameProps) => {
  const restingPercent = START_POSITION_PERCENT[startPosition] ?? 50,
    [percent, setPercent] = React.useState(restingPercent),
    [shownRestingPercent, setShownRestingPercent] = React.useState(restingPercent);

  // Re-sync when the resting position changes so the editor's Start Showing
  // Control visibly moves the seam. It only changes when a pro edits the
  // Field; visitors never see it change mid-view.
  if (shownRestingPercent !== restingPercent) {
    setShownRestingPercent(restingPercent);
    setPercent(restingPercent);
  }
  const containerRef = React.useRef<HTMLDivElement>(null),
    interactedRef = React.useRef(false),
    // Set while a handle drag is in flight so the click it ends with doesn't
    // Also jump the seam.
    draggingRef = React.useRef(false),

    percentFromClientX = (clientX: number): number => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0) {
        return restingPercent;
      }
      return clampPercent(((clientX - rect.left) / rect.width) * 100);
    },
    handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
      interactedRef.current = true;
      draggingRef.current = true;
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    handlePointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
      if (!draggingRef.current) {
        return;
      }
      setPercent(percentFromClientX(event.clientX));
    },
    handlePointerEnd = () => {
      // Cleared on the next tick so the synthetic click after a drag is ignored.
      setTimeout(() => {
        draggingRef.current = false;
      }, 0);
    },
    handleFrameClick = (event: React.MouseEvent<HTMLDivElement>) => {
      // Detail 0 = keyboard-synthesized activation (Enter/Space on the handle
      // Button, which bubbles here). Its clientX of 0 would slam the seam to 0%.
      if (draggingRef.current || event.detail === 0) {
        return;
      }
      interactedRef.current = true;
      setPercent(percentFromClientX(event.clientX));
    },
    handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      const step = (delta: number) => {
        // Both, so a hosting surface with its own arrow keys (the gallery
        // Lightbox pages photos with them) never also acts on a seam move.
        event.preventDefault();
        event.stopPropagation();
        interactedRef.current = true;
        setPercent((previous) => clampPercent(previous + delta));
      };
      if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
        step(-5);
      } else if (event.key === "ArrowRight" || event.key === "ArrowUp") {
        step(5);
      } else if (event.key === "Home") {
        event.preventDefault();
        event.stopPropagation();
        interactedRef.current = true;
        setPercent(0);
      } else if (event.key === "End") {
        event.preventDefault();
        event.stopPropagation();
        interactedRef.current = true;
        setPercent(100);
      }
    };

  // One small seam oscillation the first time the frame scrolls into view, so
  // Visitors discover the drag without a tooltip. Skipped once the visitor has
  // Interacted, and entirely under reduced motion.
  React.useEffect(() => {
    if (!wiggle || typeof IntersectionObserver === "undefined" || typeof window === "undefined") {
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const node = containerRef.current;
    if (!node) {
      return;
    }

    let frame = 0;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting) || interactedRef.current) {
        return;
      }
      observer.disconnect();
      const start = performance.now(),
        duration = 900,
        tick = (now: number) => {
          // The visitor took over mid-nudge: their handler owns the value now.
          if (interactedRef.current) {
            return;
          }
          const progress = Math.min(1, (now - start) / duration);
          // One full sine cycle: out 6%, back through rest, out -6%, home.
          setPercent(restingPercent + Math.sin(progress * Math.PI * 2) * 6);
          if (progress < 1) {
            frame = requestAnimationFrame(tick);
          }
        };
      frame = requestAnimationFrame(tick);
    });
    observer.observe(node);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [restingPercent, wiggle]);

  const loading = eager ? "eager" : "lazy",
    showsAfterChip = Boolean(afterLabel) && percent <= 100 - LABEL_FADE_MARGIN,
    showsBeforeChip = Boolean(beforeLabel) && percent >= LABEL_FADE_MARGIN;

  return (
    <div
      ref={containerRef}
      onClick={handleFrameClick}
      className={cn(
        "jobsite:relative jobsite:touch-pan-y jobsite:select-none jobsite:overflow-hidden jobsite:bg-muted",
        ASPECT_CLASS[aspectRatio] ?? ASPECT_CLASS["4:3"],
        className,
      )}
    >
      <img
        {...responsiveImageProps(afterSrc, "half_width")}
        alt={afterAlt}
        style={imagePositionStyle(afterFocus)}
        className="jobsite:absolute jobsite:inset-0 jobsite:h-full jobsite:w-full jobsite:object-cover"
        loading={loading}
        draggable={false}
      />
      <div
        className="jobsite:absolute jobsite:inset-0 jobsite:overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - percent}% 0 0)` }}
      >
        <img
          {...responsiveImageProps(beforeSrc, "half_width")}
          alt={beforeAlt}
          style={imagePositionStyle(beforeFocus)}
          className="jobsite:absolute jobsite:inset-0 jobsite:h-full jobsite:w-full jobsite:object-cover"
          loading={loading}
          draggable={false}
        />
      </div>

      {beforeLabel ? (
        <span
          aria-hidden={!showsBeforeChip}
          className={cn(
            "jobsite:absolute jobsite:left-3 jobsite:top-3 jobsite:rounded-full jobsite:bg-black/60 jobsite:px-3 jobsite:py-1 jobsite:text-[11px] jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em] jobsite:text-white jobsite:backdrop-blur jobsite:transition-opacity jobsite:duration-150 jobsite:motion-reduce:transition-none",
            showsBeforeChip ? "jobsite:opacity-100" : "jobsite:opacity-0",
          )}
        >
          {beforeLabel}
        </span>
      ) : null}
      {afterLabel ? (
        <span
          aria-hidden={!showsAfterChip}
          className={cn(
            "jobsite:absolute jobsite:right-3 jobsite:top-3 jobsite:rounded-full jobsite:bg-primary jobsite:px-3 jobsite:py-1 jobsite:text-[11px] jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em] jobsite:text-primary-foreground jobsite:transition-opacity jobsite:duration-150 jobsite:motion-reduce:transition-none",
            showsAfterChip ? "jobsite:opacity-100" : "jobsite:opacity-0",
          )}
        >
          {afterLabel}
        </span>
      ) : null}

      <div
        aria-hidden
        className="jobsite:pointer-events-none jobsite:absolute jobsite:inset-y-0 jobsite:w-0.5 jobsite:bg-white/90 jobsite:shadow-[0_0_0_1px_rgba(15,23,42,0.2)]"
        style={{ left: `${percent}%`, transform: "translateX(-50%)" }}
      />
      <button
        type="button"
        role="slider"
        aria-label={groupLabel}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(percent)}
        aria-valuetext={`${Math.round(percent)}% ${beforeLabel || "before"}, ${Math.round(100 - percent)}% ${afterLabel || "after"}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onKeyDown={handleKeyDown}
        onClick={(event) => {
          event.stopPropagation();
        }}
        // 44px hit target around a 36px grabber; touch-action none so the
        // Drag owns the gesture on the handle and only there. Focus ring is a
        // Dark-plus-white double shadow so it reads on any photo.
        className="jobsite:absolute jobsite:top-1/2 jobsite:flex jobsite:h-11 jobsite:w-11 jobsite:-translate-x-1/2 jobsite:-translate-y-1/2 jobsite:cursor-ew-resize jobsite:touch-none jobsite:items-center jobsite:justify-center jobsite:outline-none jobsite:focus-visible:[box-shadow:0_0_0_2px_rgba(15,23,42,0.7),0_0_0_4px_white] jobsite:rounded-full"
        style={{ left: `${percent}%` }}
      >
        {/* Fixed white/near-black, not theme tokens: the grabber sits on a
            photo, so it must read on any site theme. */}
        <span className="jobsite:flex jobsite:h-9 jobsite:w-9 jobsite:items-center jobsite:justify-center jobsite:rounded-full jobsite:bg-white jobsite:text-neutral-800 jobsite:shadow-[0_1px_6px_rgba(15,23,42,0.35)]">
          <ChevronsLeftRight className="jobsite:h-4 jobsite:w-4" />
        </span>
      </button>
    </div>
  );
};

export type { ComparisonAspectRatio, ComparisonStartPosition, ComparisonFrameProps };
export { ComparisonFrame };
